import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bot, ChevronDown, History, Loader2, MessageSquareText, Plus, Send, Sparkles, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { sheetService } from '../../services/sheetService';

const getSheetTitle = (sheet) => `${sheet?.title || 'Untitled Sheet'}`.trim();
const createChatId = () => (
  window.crypto?.randomUUID?.() || `workspace-chat-${Date.now()}-${Math.random().toString(36).slice(2)}`
);

const getChatDateLabel = (value) => {
  const date = value?.toDate?.() || (value ? new Date(value) : null);
  if (!date || Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric'
  }).format(date);
};

const getMentionMatch = (value = '') => {
  const match = `${value}`.match(/(^|\s)@([^@\n]*)$/);
  if (!match) return null;
  return {
    start: match.index + match[1].length,
    query: match[2].trimStart()
  };
};

const WorkspaceAiChat = ({ workspace, sheets = [], isOpen, onOpenChange }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [mentionedSheetIds, setMentionedSheetIds] = useState([]);
  const [mentionActive, setMentionActive] = useState(false);
  const [activeMentionIndex, setActiveMentionIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [chats, setChats] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [chatListOpen, setChatListOpen] = useState(false);
  const [chatListLoading, setChatListLoading] = useState(false);
  const [activeAgentId, setActiveAgentId] = useState('workspace-ai');
  const textareaRef = useRef(null);
  const messagesRef = useRef(null);

  const mentionMatch = useMemo(() => getMentionMatch(input), [input]);
  const workspaceAgent = useMemo(() => {
    if (workspace?.agentConfig?.enabled !== true) return null;
    return {
      id: 'default-agent',
      name: `${workspace.agentConfig.defaultAgentName || 'Workspace Agent'}`.trim(),
      skills: Array.isArray(workspace.agentConfig.skills) ? workspace.agentConfig.skills : []
    };
  }, [workspace?.agentConfig]);
  const activeAgent = activeAgentId === workspaceAgent?.id ? workspaceAgent : null;
  const mentionedSheets = useMemo(() => mentionedSheetIds
    .map((sheetId) => sheets.find((sheet) => sheet.id === sheetId))
    .filter(Boolean), [mentionedSheetIds, sheets]);
  const filteredSheets = useMemo(() => {
    if (!mentionActive || !mentionMatch) return [];
    const query = mentionMatch.query.toLocaleLowerCase();
    return sheets
      .filter((sheet) => !mentionedSheetIds.includes(sheet.id))
      .filter((sheet) => !query || getSheetTitle(sheet).toLocaleLowerCase().includes(query))
      .slice(0, 8);
  }, [mentionActive, mentionMatch, mentionedSheetIds, sheets]);

  useEffect(() => {
    setMessages([]);
    setInput('');
    setMentionedSheetIds([]);
    setMentionActive(false);
    setActiveChatId(null);
    setChatListOpen(false);
    setActiveAgentId('workspace-ai');
  }, [workspace?.id]);

  useEffect(() => {
    if (activeAgentId !== 'workspace-ai' && activeAgentId !== workspaceAgent?.id) {
      setActiveAgentId('workspace-ai');
    }
  }, [activeAgentId, workspaceAgent?.id]);

  useEffect(() => {
    if (!workspace?.id) {
      setChats([]);
      return undefined;
    }

    setChatListLoading(true);
    return sheetService.subscribeToWorkspaceAiChats(
      workspace.id,
      (nextChats) => {
        setChats(nextChats);
        setChatListLoading(false);
      },
      (error) => {
        setChatListLoading(false);
        toast.error(error?.message || 'Could not load saved workspace chats.');
      }
    );
  }, [workspace?.id]);

  useEffect(() => {
    setMentionedSheetIds((current) => current.filter((sheetId) =>
      sheets.some((sheet) => sheet.id === sheetId)));
  }, [sheets]);

  useEffect(() => {
    if (!messagesRef.current) return;
    messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [messages, loading]);

  useEffect(() => {
    setActiveMentionIndex(0);
  }, [mentionMatch?.query]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;
      if (chatListOpen) {
        setChatListOpen(false);
        return;
      }
      onOpenChange(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [chatListOpen, isOpen, onOpenChange]);

  const startNewChat = () => {
    setActiveChatId(null);
    setMessages([]);
    setInput('');
    setMentionedSheetIds([]);
    setMentionActive(false);
    setChatListOpen(false);
    window.requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const selectChat = (chat) => {
    setActiveChatId(chat.id);
    setMessages(Array.isArray(chat.messages) ? chat.messages : []);
    setInput('');
    setMentionedSheetIds([]);
    setMentionActive(false);
    setChatListOpen(false);
    setActiveAgentId(chat.activeAgentId === workspaceAgent?.id ? workspaceAgent.id : 'workspace-ai');
    window.requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const selectMention = (sheet) => {
    if (!mentionMatch || !sheet) return;
    const title = getSheetTitle(sheet);
    setInput(`${input.slice(0, mentionMatch.start)}@${title} `);
    setMentionedSheetIds((current) => current.includes(sheet.id) ? current : [...current, sheet.id]);
    setMentionActive(false);
    window.requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const removeMention = (sheetId) => {
    const sheet = sheets.find((item) => item.id === sheetId);
    const token = sheet ? `@${getSheetTitle(sheet)}` : '';
    setMentionedSheetIds((current) => current.filter((id) => id !== sheetId));
    if (token) setInput((current) => current.replace(token, '').replace(/\s{2,}/g, ' ').trimStart());
  };

  const handleInputChange = (event) => {
    const nextValue = event.target.value;
    setInput(nextValue);
    setMentionActive(Boolean(getMentionMatch(nextValue)));
  };

  const handleKeyDown = (event) => {
    if (mentionActive && filteredSheets.length) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveMentionIndex((current) => (current + 1) % filteredSheets.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveMentionIndex((current) => (current - 1 + filteredSheets.length) % filteredSheets.length);
        return;
      }
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        selectMention(filteredSheets[activeMentionIndex]);
        return;
      }
    }
    if (event.key === 'Escape' && mentionActive) {
      event.preventDefault();
      setMentionActive(false);
    }
  };

  const handleSend = async () => {
    const content = input.trim();
    if (!content || loading || !workspace?.id) return;
    const chatId = activeChatId || createChatId();
    const nextMessages = [...messages, {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      sheetTitles: mentionedSheets.map(getSheetTitle)
    }];
    setActiveChatId(chatId);
    setMessages(nextMessages);
    setInput('');
    setMentionActive(false);
    setLoading(true);

    try {
      await sheetService.saveWorkspaceAiChat(workspace.id, {
        id: chatId,
        messages: nextMessages,
        activeAgentId
      });
    } catch (error) {
      toast.error(error?.message || 'The chat could not be saved.');
    }

    try {
      const result = await sheetService.chatWithWorkspaceData({
        workspaceId: workspace.id,
        activeAgentId,
        mentionedSheetIds,
        messages: nextMessages.map(({ role, content: messageContent }) => ({ role, content: messageContent }))
      });
      const completedMessages = [...nextMessages, {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: `${result?.answer || 'I could not generate a response.'}`
      }];
      setMessages(completedMessages);
      try {
        await sheetService.saveWorkspaceAiChat(workspace.id, {
          id: chatId,
          messages: completedMessages,
          activeAgentId
        });
      } catch (saveError) {
        toast.error(saveError?.message || 'The latest response could not be saved.');
      }
    } catch (error) {
      toast.error(error?.message || 'Workspace AI could not answer that question.');
      const failedMessages = [...nextMessages, {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'I could not complete that request. Please try again.'
      }];
      setMessages(failedMessages);
      try {
        await sheetService.saveWorkspaceAiChat(workspace.id, {
          id: chatId,
          messages: failedMessages,
          activeAgentId
        });
      } catch {
        // The original request error is more useful than a second persistence error.
      }
    } finally {
      setLoading(false);
    }
  };

  const chatContent = (
    <div className="flex h-full min-h-0 flex-col bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={() => setChatListOpen((current) => !current)}
            className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition ${
              chatListOpen
                ? 'bg-[var(--workspace-primary-faint)] text-[var(--workspace-primary-color)]'
                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
            }`}
            aria-label={chatListOpen ? 'Close chat history' : 'Open chat history'}
            aria-expanded={chatListOpen}
          >
            <History className="h-4 w-4" />
          </button>
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[var(--workspace-primary-color)] text-white shadow-sm">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2 className="truncate text-sm font-black text-slate-950">{activeAgent?.name || 'Workspace AI'}</h2>
            <p className="truncate text-xs text-slate-500">
              {activeAgent ? `${activeAgent.skills.length} attached skill${activeAgent.skills.length === 1 ? '' : 's'}` : 'Ask across the sheets you mention'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
          aria-label="Close workspace AI"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div ref={messagesRef} className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-5" aria-live="polite">
        {messages.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white text-[var(--workspace-primary-color)] shadow-sm">
              <Bot className="h-5 w-5" />
            </div>
            <p className="mt-4 text-sm font-bold text-slate-900">Chat with your workspace</p>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              Type <span className="font-bold text-slate-700">@</span> and choose a sheet to add its permitted rows as context.
            </p>
            {sheets.length === 0 && (
              <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-700">Add an accessible sheet to start asking data questions.</p>
            )}
          </div>
        ) : messages.map((message) => (
          <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[92%] rounded-2xl px-3.5 py-3 text-sm leading-6 ${
              message.role === 'user'
                ? 'bg-[var(--workspace-primary-color)] text-white'
                : 'border border-slate-200 bg-slate-50 text-slate-700'
            }`}>
              {message.sheetTitles?.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {message.sheetTitles.map((title) => (
                    <span key={title} className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-bold">@{title}</span>
                  ))}
                </div>
              )}
              <div className="whitespace-pre-wrap break-words">{message.content}</div>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin text-[var(--workspace-primary-color)]" />
            Reading the selected sheets...
          </div>
        )}
      </div>

      <div className="border-t border-slate-200 bg-white p-4">
        {workspaceAgent && (
          <div className="mb-3">
            <label htmlFor="workspace-ai-active-agent" className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">
              Active agent
            </label>
            <select
              id="workspace-ai-active-agent"
              value={activeAgentId}
              onChange={(event) => setActiveAgentId(event.target.value)}
              disabled={loading || messages.length > 0}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none transition focus:border-[var(--workspace-primary-color)] disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500"
            >
              <option value="workspace-ai">Workspace AI</option>
              <option value={workspaceAgent.id}>{workspaceAgent.name}</option>
            </select>
            {messages.length > 0 && (
              <p className="mt-1.5 text-[11px] text-slate-500">Start a new chat to switch agents.</p>
            )}
          </div>
        )}
        {mentionedSheets.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2" aria-label="Sheets included in AI context">
            {mentionedSheets.map((sheet) => (
              <span key={sheet.id} className="inline-flex max-w-full items-center gap-1 rounded-full bg-[var(--workspace-primary-faint)] px-2.5 py-1 text-[11px] font-bold text-[var(--workspace-primary-color)]">
                <span className="truncate">@{getSheetTitle(sheet)}</span>
                <button type="button" onClick={() => removeMention(sheet.id)} aria-label={`Remove ${getSheetTitle(sheet)}`} className="rounded-full p-0.5 hover:bg-black/5">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="relative">
          {mentionActive && (
            <div className="absolute bottom-[calc(100%+0.5rem)] left-0 right-0 z-20 max-h-64 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-1.5 shadow-2xl">
              <p className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Mention a sheet</p>
              {filteredSheets.length ? filteredSheets.map((sheet, index) => (
                <button
                  key={sheet.id}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectMention(sheet)}
                  className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition ${index === activeMentionIndex ? 'bg-[var(--workspace-primary-faint)]' : 'hover:bg-slate-50'}`}
                >
                  <MessageSquareText className="h-4 w-4 shrink-0 text-[var(--workspace-primary-color)]" />
                  <span className="min-w-0 truncate text-sm font-semibold text-slate-800">{getSheetTitle(sheet)}</span>
                </button>
              )) : (
                <p className="px-3 py-4 text-xs text-slate-500">No workspace sheets match “{mentionMatch?.query || ''}”.</p>
              )}
            </div>
          )}
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            disabled={loading}
            rows={3}
            placeholder="Ask a question or type @ to mention a sheet"
            className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-3.5 py-3 pr-12 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[var(--workspace-primary-color)] focus:bg-white disabled:opacity-60"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!input.trim() || loading}
            className="absolute bottom-2.5 right-2.5 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--workspace-primary-color)] text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Send message"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
        <button
          type="button"
          onClick={() => {
            setInput((current) => `${current}${current && !current.endsWith(' ') ? ' ' : ''}@`);
            setMentionActive(true);
            window.requestAnimationFrame(() => textareaRef.current?.focus());
          }}
          disabled={!sheets.length || loading}
          className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-[var(--workspace-primary-color)] disabled:opacity-40"
        >
          @ Mention sheet
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );

  return (
    <>
      {!isOpen && (
        <button
          type="button"
          onClick={() => onOpenChange(true)}
          className="fixed bottom-5 right-5 z-[80] inline-flex items-center gap-2 rounded-2xl bg-[var(--workspace-primary-color)] px-4 py-3 text-sm font-bold text-white shadow-xl transition hover:-translate-y-0.5"
        >
          <Sparkles className="h-4 w-4" />
          Workspace AI
        </button>
      )}
      {isOpen && (
        <div
          className="fixed inset-0 z-[90] bg-slate-950/40"
          onClick={() => onOpenChange(false)}
          role="presentation"
        >
          <aside
            className="relative ml-auto flex h-full w-full overflow-hidden bg-white shadow-2xl transition-[max-width] duration-200"
            style={{ maxWidth: chatListOpen ? '53.5rem' : '34.5rem' }}
            onClick={(event) => event.stopPropagation()}
            aria-label="Workspace AI chat"
            role="dialog"
            aria-modal="true"
          >
            {chatListOpen && (
              <div className="absolute inset-y-0 left-0 z-30 flex w-[calc(100%_-_3.5rem)] shrink-0 flex-col border-r border-slate-200 bg-slate-50 shadow-xl sm:relative sm:inset-auto sm:w-80 sm:shadow-none">
                <div className="border-b border-slate-200 px-4 py-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-black text-slate-950">Chats</h3>
                      <p className="mt-0.5 text-xs text-slate-500">Your workspace history</p>
                    </div>
                    <button
                      type="button"
                      onClick={startNewChat}
                      className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-[var(--workspace-primary-color)] px-3 text-xs font-bold text-white transition hover:opacity-90"
                    >
                      <Plus className="h-4 w-4" />
                      New
                    </button>
                  </div>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto p-2">
                  {chatListLoading ? (
                    <div className="flex items-center gap-2 px-3 py-4 text-xs font-semibold text-slate-500">
                      <Loader2 className="h-4 w-4 animate-spin text-[var(--workspace-primary-color)]" />
                      Loading chats...
                    </div>
                  ) : chats.length ? chats.map((chat) => (
                    <button
                      key={chat.id}
                      type="button"
                      onClick={() => selectChat(chat)}
                      className={`mb-1 flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition ${
                        chat.id === activeChatId
                          ? 'bg-white shadow-sm ring-1 ring-slate-200'
                          : 'hover:bg-white'
                      }`}
                    >
                      <MessageSquareText className="mt-0.5 h-4 w-4 shrink-0 text-[var(--workspace-primary-color)]" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-bold text-slate-800">{chat.title || 'New chat'}</span>
                        <span className="mt-1 block truncate text-xs text-slate-500">{chat.lastMessagePreview || 'No messages yet'}</span>
                      </span>
                      <span className="shrink-0 text-[10px] font-semibold text-slate-400">{getChatDateLabel(chat.updatedAt)}</span>
                    </button>
                  )) : (
                    <div className="px-3 py-8 text-center">
                      <MessageSquareText className="mx-auto h-5 w-5 text-slate-300" />
                      <p className="mt-2 text-xs font-semibold text-slate-500">No saved chats yet</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="min-w-0 flex-1">
              {chatContent}
            </div>
          </aside>
        </div>
      )}
    </>
  );
};

export default WorkspaceAiChat;
